import { useAvailableTrainings } from '../../../../lib/api/swrApi';
import { PickerFormItem } from '../PickerForm/PickerFormTypes';
import { PickerForm } from '../PickerForm/PickerForm';
import { ASSIGN_TRAINING_DIALOG, COMMON_LABELS } from '../../../../lib/constants/labelKeys';
import { filterTrainings, isApiTraining, renderTraining, trainingIdFunction } from '../PickerForm/PickerForm.utils';
import { Api } from '../../../../lib/types/apiSpec';
import { ERROR_MESSAGES_SPLITTER } from '../../../../lib/constants/globals';
import { pipe, takeProp } from '../../../../lib/utils/functional';
import { filterByType } from '../../../../lib/utils/trainings/filterByType';
import { AssignTrainingDialogProps } from './AssignTrainingDialog.types';
import { AssignBodyType } from '../../../../lib/api/apiConfig';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';
import { isPromiseFullfiled, isPromiseRejected } from '../../../../lib/utils/promises';

const AssignTrainingDialog = ({
  params,
  assignCourse,
  assignLp,
  isModalOpened,
  dialogTitle,
  noItemsSelectedMessage,
  onCancel,
  onSuccess,
  ...rest
}: AssignTrainingDialogProps) => {
  const { data, isValidating, error, mutate } = useAvailableTrainings(params);
  const { getParsedErrorMessage } = useErrorMapping();

  const handleSubmit = async (items: PickerFormItem[]) => {
    const filteredItems = items.filter(item => isApiTraining(item)) as Api.TrainingBase[];
    const coursesIds = pipe<AssignBodyType>(filterByType(Api.LearningType.Course), takeProp('id'))(filteredItems);
    const lpIds = pipe<AssignBodyType>(filterByType(Api.LearningType.LearningPath), takeProp('id'))(filteredItems);

    const result = await Promise.allSettled<void>([
      ...(coursesIds.length ? [assignCourse(coursesIds)] : []),
      ...(lpIds.length ? [assignLp(lpIds)] : []),
    ]);

    const fulfilled = result.some(isPromiseFullfiled);
    const rejected = result.filter(isPromiseRejected);

    if (fulfilled && rejected.length) {
      mutate();
    }

    if (rejected.length) {
      if (fulfilled) {
        onSuccess(false);
      }

      throw new Error(rejected.map(item => getParsedErrorMessage(item.reason)).join(ERROR_MESSAGES_SPLITTER));
    }

    onSuccess(true);
  };

  return (
    <PickerForm
      {...rest}
      loading={isValidating}
      error={error}
      isOpen={isModalOpened}
      onSubmit={handleSubmit}
      labels={{
        selectedItemsHeadingKey: ASSIGN_TRAINING_DIALOG.SELECTED_ITEMS,
        noItemsSelectedMessage,
        dialogTitle,
        submitButtonLabelKey: ASSIGN_TRAINING_DIALOG.CONFIRM_BUTTON,
        assignedSummaryKey: ASSIGN_TRAINING_DIALOG.ASSIGNED_SUMMARY,
      }}
      onCancel={onCancel}
      value={[
        {
          titleKey: ASSIGN_TRAINING_DIALOG.TAB_LABEL,
          list: data ?? [],
          render: renderTraining,
          filterFunction: filterTrainings,
          idFunction: trainingIdFunction,
          searchPlaceholderKey: COMMON_LABELS.SEARCH.TRAINING,
        },
      ]}
    />
  );
};

export default AssignTrainingDialog;
